import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./WebsiteTrialSuccess.module.css";
import Loader2 from "../Loader2/Loader2";
import { API_BASE_URL } from "../../Store/apiStore";

function WebsiteTrialSuccess() {
  const location = useLocation();
  const navigate = useNavigate();

  const sessionId = useMemo(
    () => new URLSearchParams(location.search).get("session_id"),
    [location.search]
  );

  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSessionDetails = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/session-details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!res.ok) {
          throw new Error(`Request failed (${res.status})`);
        }

        const data = await res.json();
        const session = data?.session || data;

        if (!session) {
          throw new Error("No session returned");
        }

        if (cancelled) return;

        const paid = session?.payment_status === "paid";
        const trialing = session?.subscription?.status === "trialing";
        const active = session?.subscription?.status === "active";

        if (!paid && !trialing && !active) {
          setErrorMsg(
            "Your payment is still being processed. You'll receive a confirmation email shortly."
          );
        }

        const subscriptionId =
          session?.subscription?.id || session?.subscription || "";

        try {
          localStorage.setItem("onboardComplete", "false");
          localStorage.setItem("paymentDone", "true");
          if (subscriptionId) {
            localStorage.setItem("subcriptionIdUrl", subscriptionId);
          }
        } catch (storageErr) {
          console.warn(
            "[WebsiteTrialSuccess] Failed to persist trial flags:",
            storageErr
          );
        }

        const tokenFromApi = data?.token || data?.user?.token;
        const userFromApi = data?.user || data?.endUser;
        if (tokenFromApi) {
          try {
            localStorage.setItem("token", tokenFromApi);
            if (userFromApi?.id) {
              sessionStorage.setItem("userId", String(userFromApi.id));
            }
            setHasToken(true);
          } catch (storageErr) {
            console.warn(
              "[WebsiteTrialSuccess] Failed to persist token:",
              storageErr,
            );
          }
        }

        setSessionData(session);
      } catch (err) {
        console.error("Failed to fetch session info:", err);
        if (!cancelled) {
          setErrorMsg(
            "We couldn't load your trial details right now. Please check your email for confirmation."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSessionDetails();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionData) return;

    try {
      localStorage.setItem("websiteTrialActivated", "true");
    } catch (e) {}

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      try {
        window.gtag("event", "conversion", {
          event_category: "website_trial",
          event_label: "free_trial_started",
          transaction_id: sessionData?.id || sessionId || "unknown",
        });
      } catch (e) {}
    }
  }, [sessionData, sessionId]);

  const handleGoToDashboard = () => {
    if (hasToken) {
      navigate("/details", { replace: true });
    } else {
      navigate("/signup", { replace: true });
    }
  };

  if (!sessionId) {
    return (
      <div className={styles.container}>
        <div className={styles.Logo}>
          <img src="/svg/Rexpt-Logo.svg" alt="Rexpt-Logo" />
        </div>
        <div className={styles.errorCard}>
          <div className={styles.errorIcon}>!</div>
          <h2 className={styles.errorTitle}>Invalid session</h2>
          <p className={styles.errorText}>
            We couldn't find a valid checkout session in this link.
          </p>
          <button
            className={styles.dashboardBtn}
            style={{ background: "#6524EB", color: "#fff" }}
            onClick={() => navigate("/", { replace: true })}
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <Loader2 />;
  }

  const customerEmail =
    sessionData?.customer_details?.email ||
    sessionData?.customer?.email ||
    "your email";

  const meta = sessionData?.metadata || {};
  const planName = meta.planName || meta.plan || "Free Trial";
  const trialDays = meta.trial_days || meta.trialDays;
  const trialMins = meta.trialMins;
  const totalMins = meta.totalMins;

  const prettyPlan =
    typeof planName === "string"
      ? planName.charAt(0).toUpperCase() + planName.slice(1)
      : planName;

  return (
    <div className={styles.container}>
      <div className={styles.Logo}>
        <img src="/svg/Rexpt-Logo.svg" alt="Rexpt-Logo" />
      </div>

      <div className={styles.ThankyouMsg}>
        <div className={styles.checkWrap}>
          <div className={styles.checkCircle}>
            <svg viewBox="0 0 52 52" className={styles.checkSvg} aria-hidden="true">
              <path
                fill="none"
                stroke="#ffffff"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14 27 l8 8 l16 -18"
              />
            </svg>
          </div>
        </div>
        <div className={styles.title}>Welcome to Rexpt!</div>
        <div className={styles.subtitle}>Your free trial has started</div>
        <p className={styles.description}>
          Your free trial is active. We've sent a confirmation email to{" "}
          <strong>{customerEmail}</strong> with all the details you need.
        </p>
      </div>

      {errorMsg && <div className={styles.warningBanner}>{errorMsg}</div>}

      <div className={styles.infoBox}>
        <p>Below is some Quick Info for your Reference:</p>

        <div className={styles.row}>
          <span>Plan:</span>
          <div className={styles.Right50}>{prettyPlan}</div>
        </div>

        <div className={styles.row}>
          <span>Trial Period:</span>
          <div className={styles.Right50}>
            {trialDays ? `${trialDays} days free trial` : "Free trial"}
          </div>
        </div>

        {trialMins && (
          <div className={styles.row}>
            <span>Trial Minutes:</span>
            <div className={styles.Right50}>{trialMins} minutes included</div>
          </div>
        )}

        {totalMins && (
          <div className={styles.row}>
            <span>After Trial:</span>
            <div className={styles.Right50}>{totalMins} minutes / month</div>
          </div>
        )}

        <div className={styles.row}>
          <span>Email:</span>
          <div className={styles.Right50}>{customerEmail}</div>
        </div>

        <div style={{ textAlign: "center" }}>
          <div className={styles.activationBadge}>
            <span className={styles.badgeDot}>✓</span>
            $20 activation fee paid
          </div>
        </div>

        <div className={styles.ButtonTakeME}>
          <button className={styles.dashboardBtn} onClick={handleGoToDashboard}>
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default WebsiteTrialSuccess;
